import {
  ACTIVE_DEVICE_BUYBACK_STATUSES,
  BUYBACK_MAX_ITEMS,
  BUYBACK_MAXIMUM_PRICE,
  BUYBACK_MINIMUM_PRICE,
  type DeviceBuyback,
  type DeviceBuybackInput,
  DeviceBuybackStatus,
  deviceBuybackCollection,
  IdentificationMethod,
  isAccountHolderMatchingSeller,
  isUnderAge,
} from '@maru44/huntre-utils/src/models/deviceBuyback'
import { addDoc, collection, getDocs, serverTimestamp, where } from 'firebase/firestore'
import { useCallback } from 'react'
import { buybackDeviceTypeIds } from 'src/config'
import { ValidationError } from 'src/utils/error'
import { firestore } from 'src/utils/firebase'
import { filter, safeQuery } from 'src/utils/firestoreQuery'
import { type UsePaginatedCollectionOptions, usePaginatedCollection } from '../usePaginatedCollection'

/**
 * 買取申請の一覧と作成。
 *
 * `clients/{clientId}/deviceBuybacks` 配下だが、**パスが clients 配下なだけで
 * 同じクライアントの他メンバーには見えない**。ルールは applicantId のみで
 * 絞っているので、一覧のクエリにも applicantId が必須 (無いと list が拒否される)。
 */
export const useDeviceBuybacks = (clientId: string | undefined, applicantId: string | undefined, options: UsePaginatedCollectionOptions = {}) => {
  const listDeviceBuybacks = usePaginatedCollection<DeviceBuyback>(deviceBuybackCollection(clientId ?? '-'), {
    ...options,
    additionalConstraints: [where('applicantId', '==', applicantId ?? '')],
    disabled: !clientId || !applicantId || options.disabled,
  })

  const createDeviceBuyback = useCallback(
    async (input: DeviceBuybackInput) => {
      try {
        if (input.devices.length === 0) {
          throw new ValidationError('デバイスを選択してください')
        }
        if (input.devices.length > BUYBACK_MAX_ITEMS) {
          throw new ValidationError(`1 回の申請に出せるのは ${BUYBACK_MAX_ITEMS} 台までです`)
        }
        if (input.devices.some((v) => !buybackDeviceTypeIds.includes(v.deviceTypeId))) {
          throw new ValidationError('買取対象外のデバイスが含まれています')
        }
        if (isUnderAge(input.seller.birthday, new Date())) {
          throw new ValidationError('未成年の方からは買い取りできません')
        }
        // 口座名義とフリガナが一致しないと、非対面の本人確認が成立しない。
        // サーバ (validateBuybackApplication) も同じ判定で却下するが、
        // **申し込んだあとに却下されるより、ここで止めたほうが親切**
        if (!isAccountHolderMatchingSeller(input.seller.nameKana, input.bankAccount?.accountHolderKana)) {
          throw new ValidationError('振込先口座の名義がお名前のフリガナと一致しません。ご本人名義の口座をご入力ください')
        }

        // 自分が同じデバイスで申請中なら弾く。
        // 他人の申請は見えない (ルールで applicantId に絞っている) ので、
        // 最終的な判定はサーバ側の validateBuybackApplication が行う
        const deviceIds = input.devices.map((v) => v.deviceId)
        const dup = await getDocs(
          safeQuery(
            collection(firestore, deviceBuybackCollection(input.clientId)),
            filter('applicantId', '==', applicantId ?? ''),
            filter('deviceIds', 'array-contains-any', deviceIds),
          ),
        )
        if (dup.docs.some((d) => ACTIVE_DEVICE_BUYBACK_STATUSES.includes((d.data() as { status: DeviceBuybackStatus }).status))) {
          throw new ValidationError('既に買取申請中のデバイスが含まれています')
        }

        const now = serverTimestamp()
        // ルールが request.resource.data.clientId == パスの clientId を課すので、
        // パスとフィールドは同じ値から作る
        const ref = await addDoc(collection(firestore, deviceBuybackCollection(input.clientId)), {
          clientId: input.clientId,
          items: input.devices.map((v) => ({ deviceId: v.deviceId, deviceTypeId: v.deviceTypeId, assessedPrice: null })),
          // items の射影。Firestore は配列内オブジェクトのフィールドで検索できないので、
          // 重複チェックを array-contains-any で引けるように別に持つ
          deviceIds,
          applicantId: applicantId ?? '',
          status: DeviceBuybackStatus.Applied,

          seller: { ...input.seller, ageAtTransaction: null },
          identificationKind: input.identificationKind,
          identificationMethod: IdentificationMethod.ImageAndBankTransfer,
          // 画像は申請を作ってから面ごとに上げる。Storage のルールが
          // clients/{clientId}/deviceBuybacks/{id}.applicantId を見るため
          identificationImagePaths: {},
          identificationPurgedAt: null,
          verifiedAt: null,
          verifiedBy: null,

          approvedAt: null,
          approvedBy: null,
          shippingDeadline: null,
          shippingReminderSentAt: null,
          needsFixReason: '',
          needsFixCount: 0,

          bankAccount: input.bankAccount,
          bankAccountPurgedAt: null,
          // 名義一致の判定はサーバが入れる。ルールが null を要求している
          accountHolderMatchesSeller: null,

          minimumPrice: BUYBACK_MINIMUM_PRICE,
          maximumPrice: BUYBACK_MAXIMUM_PRICE,
          agreedAt: now,
          totalPrice: null,
          assessedAt: null,
          assessedBy: null,
          paidAt: null,
          paidBy: null,

          returnAddress: input.returnAddress,
          notifiedStatuses: [],
          applicantNote: input.applicantNote,
          adminNote: '',
          rejectedReason: '',

          createdAt: now,
          updatedAt: now,
        })
        return ref.id
      } catch (e) {
        console.error(e)
        throw e
      }
    },
    [applicantId, clientId],
  )

  /**
   * 買取に出せるデバイス = 対象 deviceType かつ、自分の進行中の申請が無いもの。
   *
   * ステータスの絞り込みはクライアント側で行う (論理和は 1 つまで。
   * 詳細と enforcement は src/utils/firestoreQuery.ts)。
   *
   * 自分の申請しか見えない (ルールで applicantId に絞っている) ので、
   * 同じクライアントの別メンバーが先に申請していた場合はここでは弾けない。
   * そちらはサーバ側の validateBuybackApplication が受け持つ。
   */
  const listSellableDeviceIds = useCallback(
    async (deviceIds: string[]) => {
      if (deviceIds.length === 0 || !applicantId || !clientId) return []
      const applied = new Set<string>()
      // where in は 30 件まで
      const CHUNK = 30
      for (let i = 0; i < deviceIds.length; i += CHUNK) {
        const snap = await getDocs(
          safeQuery(
            collection(firestore, deviceBuybackCollection(clientId)),
            filter('applicantId', '==', applicantId),
            filter('deviceIds', 'array-contains-any', deviceIds.slice(i, i + CHUNK)),
          ),
        )
        for (const d of snap.docs) {
          const data = d.data() as { deviceIds: string[]; status: DeviceBuybackStatus }
          if (ACTIVE_DEVICE_BUYBACK_STATUSES.includes(data.status)) {
            for (const id of data.deviceIds ?? []) applied.add(id)
          }
        }
      }
      return deviceIds.filter((id) => !applied.has(id))
    },
    [applicantId, clientId],
  )

  return { listDeviceBuybacks, createDeviceBuyback, listSellableDeviceIds }
}
